import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { Market, PriceRunLine } from "./api";
import { humanDate, money } from "./format";
import {
  anchorNote, convertedNote, deviationLabel, feeReasonBody, feeReasonRefusal, feeReasonText, feesMarked, notSetText, offersFeeReason,
  recordFeeReason, recordText, RRP_COPY, RRP_KEY, type RrpLookup, type RrpRow,
} from "./rrp";

/**
 * The RRP line inside the Price cell (approved design M25 `rrpCellHtml`; R1b kept its place, "not set until release 4").
 * Every figure is the overlay's: the RRP, the deviation of the committed price, the parity risk, the exception it stands
 * under. A saved price is judged by C2 when it is saved, so the cell shows the exception the saved line carries, not a
 * deviation worked out in the browser. Pending renders nothing (the grid is not held back for it).
 * C1 v1.37: a reason recorded on a price above its RRP marks the deviation ", fees" and reads under it; a Shopify row
 * above its RRP with no reason offers "Record fee reason" (M25), which C2 v1.39 records on the committed price.
 */
export function RrpCell({ lookup, market, saved, areaName }: { lookup: RrpLookup; market: Market; saved: PriceRunLine | null; areaName: string }) {
  const savedException = saved?.rrp_exception_reason && saved.rrp_exception_until
    ? <div className="text-xs text-ks-warn">{RRP_COPY.savedException(saved.rrp_exception_reason, humanDate(saved.rrp_exception_until))}</div>
    : null;
  if (lookup.kind === "pending") return savedException;
  if (lookup.kind === "failed") return <><div className="text-xs text-ks-grey-1">{RRP_COPY.unreadCell}</div>{savedException}</>;
  if (lookup.kind === "none") return savedException;
  const r = lookup.row;
  if (r.rrp == null) return <><div className="text-xs text-ks-grey-1">{notSetText(r, market)}</div>{savedException}</>;
  const converted = convertedNote(r);
  return (
    <div className="mt-1">
      <div className="text-xs font-semibold tabular-nums text-ks-ink">{RRP_COPY.rrp(money(r.rrp, r.currency))}</div>
      <div className="text-xs text-ks-grey-1">{anchorNote(lookup)}</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {r.deviation_pct != null
          ? <Badge variant="outline" className={r.parity_risk ? "border-ks-warn text-ks-warn" : undefined}>{deviationLabel(r.deviation_pct, feesMarked(r))}</Badge>
          : <span className="text-xs text-ks-grey-1">{r.comparable === false ? RRP_COPY.notComparable(r.price_currency) : RRP_COPY.noPrice}</span>}
        {r.parity_risk && <Badge variant="outline" className="border-ks-warn text-ks-warn">{RRP_COPY.parityRisk}</Badge>}
      </div>
      {converted && <div className="text-xs text-ks-grey-1">{converted}</div>}
      {r.fee_reason && <div className="text-xs text-ks-grey-1">{feeReasonText(r.fee_reason)}</div>}
      {r.channel === "shopify" && r.above_rrp === true && <FeeReasonLink row={r} areaName={areaName} />}
      {r.exception && (
        <div className="text-xs text-ks-grey-1">
          {recordText(r.exception)}{r.exception.ended && <> <Badge variant="outline">{RRP_COPY.ended}</Badge></>}
        </div>
      )}
      {!r.exception && r.parity_risk && <div className="text-xs text-ks-warn">{RRP_COPY.noException}</div>}
      {r.is_anchor && r.anchor_drift && r.anchor_price && <div className="text-xs text-ks-warn">{RRP_COPY.drift(money(r.anchor_price, r.currency))}</div>}
      {savedException}
    </div>
  );
}

/** "Record fee reason" on a Shopify row above its RRP (M25): the link opens a reason field in the cell; Save sends it to C2,
 * which records it on the committed price and answers the row, and the overlay is read again so the cell shows it. The
 * block stays mounted while the row stands above its RRP so the status line survives the link's going, and renders
 * nothing when it has no link, no form and no note; the status note clears once the overlay carries the reason (the reason
 * line is the confirmation). Every click and key stops at the cell: the row's own click opens the store's rows or the
 * drawer; Enter saves and Escape cancels. A refusal is an alert; a success a status. */
function FeeReasonLink({ row, areaName }: { row: RrpRow; areaName: string }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const recorded = row.fee_reason !== null;
  useEffect(() => { if (recorded) setNote(null); }, [recorded]);
  const record = useMutation({
    mutationFn: (text: string) => recordFeeReason(feeReasonBody(row, text)),
    onSuccess: (fresh: RrpRow) => {
      setOpen(false); setReason("");
      setNote({ tone: "ok", text: RRP_COPY.feeReason.saved(fresh.fee_reason?.reason ?? reason.trim()) });
    },
    onError: (error) => setNote({ tone: "err", text: feeReasonRefusal(error, areaName) }),
    // the overlay is the truth either way: a success shows the reason, a refusal names a row that moved
    onSettled: () => { void client.invalidateQueries({ queryKey: RRP_KEY }); },
  });
  const submit = () => {
    if (!reason.trim()) { setNote({ tone: "err", text: RRP_COPY.feeReason.empty }); return; }
    record.mutate(reason);
  };
  const id = `pp-fee-reason-${row.xero_sku}-${row.marketplace_id}`;
  if (!offersFeeReason(row) && !open && !note) return null;
  return (
    <div className="mt-1" data-fee-reason>
      {offersFeeReason(row) && !open && (
        <button type="button" className="text-xs text-ks-purple underline underline-offset-2" data-nonmutating data-fee-reason-link
          onClick={(e) => { e.stopPropagation(); setOpen(true); setNote(null); }}>{RRP_COPY.feeReason.link}</button>
      )}
      {open && (
        <div className="flex flex-col gap-1" data-fee-reason-form>
          <Label htmlFor={id} className="text-xs">{RRP_COPY.feeReason.label}</Label>
          <Input id={id} value={reason} maxLength={500} placeholder={RRP_COPY.feeReason.placeholder} onClick={(e) => e.stopPropagation()}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); submit(); } else if (e.key === "Escape") { setOpen(false); setReason(""); } }} />
          <div className="flex gap-1">
            <Button size="sm" disabled={record.isPending} onClick={(e) => { e.stopPropagation(); submit(); }}>{RRP_COPY.feeReason.save}</Button>
            <Button size="sm" variant="outline" data-nonmutating disabled={record.isPending}
              onClick={(e) => { e.stopPropagation(); setOpen(false); setReason(""); }}>{RRP_COPY.feeReason.cancel}</Button>
          </div>
        </div>
      )}
      {note && <p role={note.tone === "err" ? "alert" : "status"} className={`text-xs ${note.tone === "err" ? "text-ks-danger" : "text-ks-grey-1"}`}>{note.text}</p>}
    </div>
  );
}
